import { useState } from "react"
import { t } from "@lingui/core/macro"
import { isMobile } from "react-device-detect"
import {
    useAppContext,
    useFeatureFlag,
    usePerformToggle,
} from "../../../hooks/appContext"
import { useAppDispatch } from "../../../store/hooks"
import { addPageAction } from "../../../utils/newrelic"
import {
    BlackClose,
    Expand,
    Filter,
    NewMessage,
} from "../../common/atoms/Icons/Chat"
import { AlertCircle } from "../../common/atoms/Icons/Others"
import { IconButton } from "../common/IconButton/IconButton"
import { ProfilePicture } from "../common/ProfilePicture/ProfilePicture"
import { useMessagingView } from "../hooks"
import { MessageProfileAlert } from "../MessageProfileAlert/MessageProfileAlert"
import { useMessagingContext } from "../MessagingContext"
import { ThreadList } from "../ThreadList/ThreadList"
import { ThreadSearch } from "../ThreadSearch"
import { classNames } from "../utils"
import { FeedbackHelper } from "./FeedbackHelper"
import styles from "./Inbox.module.scss"
import { GeneralViewModel } from "./viewModels/GeneralViewModel"
import { UnifiedViewModel } from "./viewModels/UnifiedViewModel"

type Props = {
    onClose: () => void
    onNewMessage: () => void
}

const SHOW_FEEDBACK_MODAL_ON_LOAD = false

const mobileHeaderIcons = (
    userFeedbackClick: () => void,
    newMessageClick: () => void,
    backClick: () => void,
) => (
    <div className={styles.inboxHeaderButtons}>
        <IconButton
            icon={<AlertCircle height={20} width={20} />}
            onClick={userFeedbackClick}
            data-testid="user-feedback-icon"
            className={styles.alertCircle}
        />
        <IconButton
            icon={
                <NewMessage height={21} width={21} onClick={newMessageClick} />
            }
        />
        <IconButton
            icon={<BlackClose height={24} width={24} />}
            onClick={backClick}
            data-testid="close-button"
        />
    </div>
)

const desktopInboxHeaderIcons = (
    isDsktpNewMessageActive: boolean,
    isDsktpUnifiedMessagingActive: boolean,
    handleClickUserfeedbackIcon: () => void,
    handleClickPopout: () => void,
    newMessageClick: () => void,
    backClick: () => void,
) => (
    <div className={classNames(styles.inboxHeaderButtons, styles.desktop)}>
        <IconButton
            icon={<AlertCircle height={20} width={20} />}
            onClick={handleClickUserfeedbackIcon}
            data-testid="user-feedback-icon"
            className={styles.alertCircle}
        />
        {isDsktpNewMessageActive && (
            <IconButton
                icon={
                    <NewMessage
                        height={20}
                        width={20}
                        onClick={newMessageClick}
                    />
                }
            />
        )}
        {isDsktpUnifiedMessagingActive && (
            <IconButton
                icon={<Expand height={20} width={20} />}
                onClick={handleClickPopout}
            />
        )}
        <IconButton
            icon={<BlackClose height={22} width={22} />}
            onClick={backClick}
        />
    </div>
)

export const Inbox: React.FC<Props> = ({ onClose, onNewMessage }: Props) => {
    const messagingView = useMessagingView()
    const dispatch = useAppDispatch()

    const vmProps = {
        dispatch,
    }

    const { newMessageClick } = messagingView.isDesktopUnified
        ? UnifiedViewModel(vmProps)
        : GeneralViewModel(vmProps)

    const { dmPopout } = useMessagingContext()
    const [showProfileAlert, setShowProfileAlert] = useState(true)
    const [userClickedFeedbackIcon, setUserClickedFeedbackIcon] =
        useState(false)
    const [showUserFeedbackModal, setShowUserFeedbackModal] = useState(
        SHOW_FEEDBACK_MODAL_ON_LOAD,
    )
    const loggedInUsername = useAppContext().context.logged_in_user?.username
    const isDmSettingsActive = usePerformToggle("DmSettings").active
    const isDmImpOrderingActive = usePerformToggle("DmImpOrdering").active
    const isDesktopDmActive = usePerformToggle("DesktopDm").active
    const [hideHeader, setHideHeader] = useState(false)
    const [filter, setFilter] = useState("")
    const [useConversationFeatures, setUseConversationFeatures] = useState(true)
    const isDsktpNewMessageActive = useFeatureFlag("DsktpNewMessage").active
    const isDsktpUnifiedMessagingActive = useFeatureFlag(
        "DsktpUnifiedMessaging",
    ).active

    const handleClickPopout = () => {
        dmPopout.show()
        onClose()
    }

    const handleSearchFocus = (focused: boolean) => {
        setHideHeader(focused)
        if (!focused) {
            setFilter("")
        }
    }

    const handleClickUserfeedbackIcon = () => {
        setUserClickedFeedbackIcon(true)
        setShowUserFeedbackModal(true)
        addPageAction("DmFeedback", {
            actionType: "open",
        })
    }

    const getHeaderIcons = () => {
        if (messagingView.isMobile) {
            return mobileHeaderIcons(
                handleClickUserfeedbackIcon,
                newMessageClick,
                onClose,
            )
        }
        if (isDesktopDmActive && messagingView.isDesktopInbox) {
            return desktopInboxHeaderIcons(
                isDsktpNewMessageActive,
                isDsktpUnifiedMessagingActive,
                handleClickUserfeedbackIcon,
                handleClickPopout,
                newMessageClick,
                onClose,
            )
        }
        if (messagingView.isDesktopUnified) {
            return <NewMessage height={21} width={21} onClick={onNewMessage} />
        }

        return null
    }

    return (
        <div
            className={classNames(styles.inbox, {
                [styles.mobile]: messagingView.isMobile,
                [styles.desktopUnified]: messagingView.isDesktopUnified,
                [styles.desktopInbox]: messagingView.isDesktopInbox,
            })}
            data-testid="inbox"
        >
            <div
                className={classNames(styles.inboxHeader, {
                    [styles.searchFocused]:
                        hideHeader && messagingView.isMobile,
                    [styles.desktopInbox]: messagingView.isDesktopInbox,
                })}
                onClick={(e) => {
                    if (
                        messagingView.isDesktopInbox &&
                        e.target === e.currentTarget
                    ) {
                        onClose()
                    }
                }}
            >
                <div className={styles.inboxHeaderTitle}>{t`Messages`}</div>
                {getHeaderIcons()}
            </div>
            <div className={styles.inboxContent}>
                <div
                    className={classNames(styles.inboxFilter, {
                        [styles.focused]: hideHeader,
                    })}
                >
                    {isDmSettingsActive && (
                        <ProfilePicture
                            user={{
                                // TODO: [MSG-414] get the actual user data
                                username: loggedInUsername ?? "",
                                inFanclub: false,
                                hasTokens: false,
                                isMod: false,
                                tippedRecently: false,
                                tippedALotRecently: false,
                                tippedTonsRecently: false,
                            }}
                            size={32}
                        />
                    )}
                    {useConversationFeatures && (
                        <ThreadSearch
                            focusCallback={handleSearchFocus}
                            onTyping={setFilter}
                            inputProps={{
                                maxLength: 30,
                            }}
                        />
                    )}
                    {isDmImpOrderingActive && (
                        <button
                            className={styles.filterButton}
                            data-testid="filter-btn"
                        >
                            <Filter />
                        </button>
                    )}
                </div>
                {messagingView.isMobile && isDmSettingsActive && (
                    <MessageProfileAlert
                        show={showProfileAlert}
                        onClose={() => setShowProfileAlert(false)}
                    />
                )}
                <ThreadList
                    isMobile={isMobile}
                    filteredName={filter}
                    searchFocused={hideHeader}
                    permissionUpdate={setUseConversationFeatures}
                />
                <FeedbackHelper
                    showFeedback={showUserFeedbackModal}
                    setShowFeedback={setShowUserFeedbackModal}
                    userClickedFeedbackIcon={userClickedFeedbackIcon}
                />
            </div>
        </div>
    )
}
